import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";

/**
 * Encapsulates dining-session data loading + polling for status updates.
 *
 * Returns: reservation, menu, tables, orders, lastUpdated, refreshing,
 *          initialLoading, refreshAll
 */
export function useDiningSession(reservationId) {
    const [reservation, setReservation] = useState(null);
    const [menu, setMenu] = useState([]);
    const [tables, setTables] = useState([]);
    const [orders, setOrders] = useState([]);
    const [initialLoading, setInitialLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [lastUpdated, setLastUpdated] = useState(null);

    const fetchReservation = useCallback(async () => {
        const { data } = await api.get(`/reservations/${reservationId}`);
        setReservation(data);
        return data;
    }, [reservationId]);

    const fetchOrders = useCallback(async () => {
        try {
            const { data } = await api.get(`/reservations/${reservationId}/orders`);
            setOrders(data || []);
            setLastUpdated(new Date());
        } catch (error) {
            console.warn("[dining] fetchOrders failed", error?.message);
        }
    }, [reservationId]);

    const fetchMenuAndTables = useCallback(async (restaurantId) => {
        if (!restaurantId) return;
        try {
            const [m, t] = await Promise.all([
                api.get(`/restaurants/${restaurantId}/menu`),
                api.get(`/restaurants/${restaurantId}/tables`),
            ]);
            setMenu(m.data || []);
            setTables(t.data || []);
        } catch (error) {
            console.warn("[dining] fetchMenuAndTables failed", error?.message);
        }
    }, []);

    // Initial bootstrap
    useEffect(() => {
        let active = true;
        (async () => {
            try {
                setInitialLoading(true);
                const res = await fetchReservation();
                if (!active) return;
                if (res?.restaurant_id) {
                    await fetchMenuAndTables(res.restaurant_id);
                }
                await fetchOrders();
            } catch (error) {
                console.warn("[dining] initial load failed", error?.message);
            } finally {
                if (active) setInitialLoading(false);
            }
        })();
        return () => {
            active = false;
        };
    }, [fetchReservation, fetchMenuAndTables, fetchOrders]);

    // Poll while session is active
    const isActive = reservation && ["checked_in", "seated"].includes(reservation.status);
    useEffect(() => {
        if (!isActive) return undefined;
        const id = setInterval(async () => {
            setRefreshing(true);
            try {
                await Promise.all([fetchOrders(), fetchReservation()]);
            } finally {
                setRefreshing(false);
            }
        }, 4000);
        return () => clearInterval(id);
    }, [isActive, fetchOrders, fetchReservation]);

    const refreshAll = useCallback(async () => {
        setRefreshing(true);
        try {
            await Promise.all([fetchOrders(), fetchReservation()]);
        } finally {
            setRefreshing(false);
        }
    }, [fetchOrders, fetchReservation]);

    return {
        reservation,
        menu,
        tables,
        orders,
        initialLoading,
        refreshing,
        lastUpdated,
        refreshAll,
        fetchReservation,
        fetchOrders,
    };
}
